import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeftIcon, MapPinIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { ProductImage } from '../components/shop/ProductImage';
import { ProductCard } from '../components/shop/ProductCard';
import { Button } from '../components/Button';
import { NotFound } from './NotFound';
import { products } from '../data/products';
import { contact } from '../data/site';
import { useCart } from '../contexts/CartContext';
import { formatMoney, formatPriceRange } from '../utils/price';
export function ProductDetail() {
  const {
    slug
  } = useParams();
  const navigate = useNavigate();
  const product = products.find(p => p.slug === slug);
  const {
    addItem
  } = useCart();
  const [option, setOption] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState<string | null>(null);
  if (!product) return <NotFound data-id="element-430" data-mp="3jwkaevq3cts1" />;
  const selected = product.options?.find(o => o.label === option);
  const unitPrice = selected?.price ?? product.price;
  const related = products.filter(p => p.slug !== product.slug && !!p.inHouse === !!product.inHouse).slice(0, 3);
  const handleAdd = () => {
    if (product.options && !selected) {
      setError(`Please choose a ${product.optionLabel?.toLowerCase() ?? 'option'}.`);
      return;
    }
    if (typeof unitPrice !== 'number') return;
    addItem({
      slug: product.slug,
      name: product.name,
      option: selected?.label,
      price: unitPrice,
      qty,
      image: product.image
    });
    setError(null);
    toast.success(`${product.name} added to cart`, {
      action: {
        label: 'View cart',
        onClick: () => navigate('/cart')
      }
    });
  };
  return <>
      <section className="mx-auto max-w-6xl px-6 pb-20 pt-32 xl:pt-44" data-id="element-431" data-mp="2e7u65n9tlsy">
        <Link to="/shop" className="inline-flex items-center gap-2 font-slab text-xs font-bold uppercase tracking-[0.2em] text-ink-muted hover:text-white" data-id="element-432" data-mp="2i6f6ba0jvom3">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-433" data-mp="2345jvmhuvbhh" />
          Back to shop
        </Link>
        <div className="mt-10 grid gap-12 md:grid-cols-2" data-id="element-434" data-mp="1z2tuni2w8s9t">
          <div className="aspect-square overflow-hidden bg-ink-surface" data-id="element-435" data-mp="1z3dbswkpzcms">
            <ProductImage product={product} data-id="element-436" data-mp="1uatqfdfsjd7f" />
          </div>
          <div data-id="element-437" data-mp="1pd0iofyuab51">
            <p className="text-xs uppercase tracking-[0.15em] text-ink-faint" data-id="element-438" data-mp="1pda73z48ka8m">{product.categories.join(', ')}</p>
            <h1 className="mt-2 font-slab text-3xl font-bold uppercase leading-tight tracking-[0.1em] md:text-4xl" data-id="element-439" data-mp="1keqly0xgv2hb">{product.name}</h1>
            {!product.inHouse && <p className="mt-4 font-slab text-2xl tracking-wider" data-id="element-440" data-mp="1bcpp3565aig6">{unitPrice !== undefined && selected ? formatMoney(unitPrice) : formatPriceRange(product)}</p>}
            <div className="mt-6 space-y-4 leading-relaxed text-ink-muted" data-id="element-441" data-mp="16g93ahmf4g69">
              {product.description.map(d => <p key={d.slice(0, 24)} data-id="element-442" data-mp="30s73xv6nw3qj">{d}</p>)}
            </div>

            {product.inHouse ? <div className="mt-8 flex gap-4 border border-ink-line p-6" data-id="element-443" data-mp="38c85ep9k3i14">
                <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" data-id="element-444" data-mp="3da9ovbaw9jd4" />
                <p className="text-sm leading-relaxed text-ink-muted" data-id="element-445" data-mp="3d86sm9j5etzy">
                  <span className="font-slab text-xs font-bold uppercase tracking-[0.2em] text-white" data-id="element-446" data-mp="3i21i5tlfq36y">Available in-store only</span>
                  <br data-id="element-447" data-mp="2pckiqbzedm18" />
                  {contact.addressLine1}, {contact.addressLine2}
                </p>
              </div> : product.soldOut ? <p className="mt-8 inline-block border border-ink-line px-4 py-3 font-slab text-xs font-bold uppercase tracking-[0.2em] text-ink-muted" data-id="element-448" data-mp="2uc73r7lgw6kl">
                Currently sold out
              </p> : <div className="mt-8 space-y-6" data-id="element-449" data-mp="33a0p5nhp1fsz">
                {product.options && <fieldset data-id="element-450" data-mp="emlz62v2wj9o">
                    <legend className="font-slab text-xs font-bold uppercase tracking-[0.2em]" data-id="element-451" data-mp="ilusj6m7if4p">{product.optionLabel}</legend>
                    <div className="mt-3 flex flex-wrap gap-2" data-id="element-452" data-mp="h45yaio6s8bp">
                      {product.options.map(o => <button key={o.label} type="button" onClick={() => {
                  setOption(o.label);
                  setError(null);
                }} aria-pressed={option === o.label} className={`min-w-[56px] border px-4 py-2.5 font-slab text-sm tracking-wider transition-colors duration-150 ease-out ${option === o.label ? 'border-white bg-white text-ink' : 'border-ink-line hover:border-white'}`} data-id="element-453" data-mp="c75yuqlue3tk">
                          {o.label}
                        </button>)}
                    </div>
                    {error && <p className="mt-2 text-sm text-red-300" data-id="element-454" data-mp="1etr1qqty3rqr">{error}</p>}
                  </fieldset>}
                <div className="flex flex-wrap items-stretch gap-3" data-id="element-455" data-mp="1xhjed7ev1muo">
                  <div className="flex items-center border border-ink-line" data-id="element-456" data-mp="22gcwxe90841p">
                    <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))} className="px-3 py-3 hover:opacity-60" aria-label="Decrease quantity" data-id="element-457" data-mp="1oglkhhy4yvpt">
                      <MinusIcon className="h-4 w-4" data-id="element-458" data-mp="1tdljj8wkiy7f" />
                    </button>
                    <span className="w-8 text-center font-slab" aria-live="polite" data-id="element-459" data-mp="1eoz3f4z4a2f5">
                      {qty}
                    </span>
                    <button type="button" onClick={() => setQty(q => q + 1)} className="px-3 py-3 hover:opacity-60" aria-label="Increase quantity" data-id="element-460" data-mp="15pu5o0vdqd41">
                      <PlusIcon className="h-4 w-4" data-id="element-461" data-mp="19s8pan6vhgwd" />
                    </button>
                  </div>
                  <Button onClick={handleAdd} data-id="element-462" data-mp="vs69d6i4kxjr">Add to cart</Button>
                </div>
              </div>}
          </div>
        </div>
      </section>

      {related.length > 0 && <section className="border-t border-ink-line py-16" data-id="element-463" data-mp="32gq103h4zqcc">
          <div className="mx-auto max-w-6xl px-6" data-id="element-464" data-mp="2xook2490xbfz">
            <h2 className="font-slab text-2xl font-bold uppercase tracking-[0.15em]" data-id="element-465" data-mp="2xnx0xswm566l">You may also like</h2>
            <div className="mt-10 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-466" data-mp="2tlihb6l4e2e9">
              {related.map(p => <ProductCard key={p.slug} product={p} data-id="element-467" data-mp="3leivchdp2ry4" />)}
            </div>
          </div>
        </section>}
    </>;
}